import { Injectable } from "@angular/core";
import { NavigationCancel, Router } from "@angular/router";
import { Subject, Subscription } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import { WebSocketSubject, webSocket } from "rxjs/webSocket";
import { Store } from "@ngrx/store";
import { Actions, ofType } from "@ngrx/effects";
import { DynamicDialogRef } from "primeng/dynamicdialog";
import { Certificate, createDetachedSignature, createHash, getUserCertificates } from "crypto-pro";
import { environment } from "src/environments/environment";

import { checkMobileEds } from "@app/app.actions";
import * as SignActions from "@app/user/documents/documents-store/documents-sign.actions";
import * as DocumentActions from "@app/user/documents/documents-store/documents.actions";
import * as SCCryptoActions from "@app/user/customization/customization-store/sccrypto.actions";
import { UserRoutingService } from "@app/user/user-core/services/user-routing.service";
import { UserBackendService } from "@app/user/user-core/services/user-backend.service";
import { OverlayRootService } from "./overlay-root.service";
import { CertificateListPopupComponent } from "@shared/shared-popups/certificate-list-popup/certificate-list-popup.component";
import { CertificationMobilePopupComponent } from "@shared/shared-popups/certification-mobile-popup/certification-mobile-popup.component";
import { SignatureMobilePopupComponent } from "@shared/shared-popups/signature-mobile-popup/signature-mobile-popup.component";
import { OverlayComponent } from "@shared/overlay/overlay.component";
import { PendingComponent } from "@shared/pending/pending.component";
import { AttachSignParams, MobileEdsMsg, MobileMsgMetaData } from "@helper/abstraction/sccrypto";
import { NCASignResponseDto, nCASign } from "@helper/abstraction/nca-sign";
import { DocumentType } from "@helper/abstraction/documents";
import { DraftDto } from "@helper/abstraction/draft";
import { TranslationService } from "./translation.service";
import { OverlayService } from "./overlay.service";

export type NcaActionType = "BIND" | "SIGN";

export interface NcaActionDto {
	type?: NcaActionType;
	draftType?: any;
	document?: any;
	bindData?: {
		serialNumber?: string;
		xmlBase64?: string;
	}
};

export interface SignDto extends DraftDto {
  method?: string;
  documentType?: DocumentType;
  roseuDocumentType?: { id: string; name: string };
  mobileEdsMsg?: MobileEdsMsg;
  metaData?: MobileMsgMetaData;
}

export interface MassSignDto extends SignDto {
	roseuDocumentType?: { id: string; name: string };
	draft?: DraftDto[];
}

@Injectable()
export class CryptoSignService {
	public ref?: DynamicDialogRef;
	public errorProcessedDraftId?: string;
	public certificateType?: string | null;
	private component: OverlayComponent;
	private env = environment;
	private wssUrl = "wss://127.0.0.1:13579/";
	private base64 = true;
    private subscription?: Subscription;
	private wsSubject$$?: WebSocketSubject<any>;
	private unsubscribe$$: Subject<void> = new Subject<void>();

	constructor(
		private actions$: Actions,
		private router: Router,
		private store: Store,
		private overlayService: OverlayService,
		private translationService: TranslationService,
		private userBackendService: UserBackendService,
		private userRoutingService: UserRoutingService,
		private overlayRootService: OverlayRootService,
	) {
		this.component = this.overlayRootService.getRootOverlayComponent();
		this.router.events.pipe(
			filter(ev => ev instanceof NavigationCancel),
			takeUntil(this.unsubscribe$$)).subscribe(event => {
				if ((event as NavigationCancel).url === "/user/logout") {
					this.ref?.destroy();
					this.wsSubject$$?.unsubscribe();
				}
			});
		this.actions$.pipe(
			ofType(checkMobileEds),
			takeUntil(this.unsubscribe$$)
		).subscribe((value) => this.signDocument(value.signDto));
		this.actions$.pipe(
			ofType(DocumentActions.signCryptoProError),
			takeUntil(this.unsubscribe$$)
		).subscribe((value) => this.catchCryptoSign(value.errors as string, false));

		this.actions$.pipe(
			ofType(
				SCCryptoActions.bindCertificateToUserError,
				SCCryptoActions.bindCertificateToUserSuccess,
			),
			takeUntil(this.unsubscribe$$)
		).subscribe(() => {
			this.openLoadingPopup(false);
		});
	}

	public signDocument(signDto?: SignDto): void {
		this.certificateType = sessionStorage.getItem("CertificateType");
		switch (this.env.project) {
			case "KZ":
				this.signNcaCrypto(signDto!);
				break;
			case "ME-RU":
				this.signCryptoPro1(signDto!);
				break;
			case "TK":
				break;
			default: {
				switch (this.certificateType) {
					case "certificate": {
						this.signSCCrypto(signDto!);
					};
					break;
					case "certificateMobile": {
						this.signingMobileEds(signDto?.mobileEdsMsg!, signDto?.metaData!);
					};
					break;
					default: this.showErrorMessage(this.translationService.getTranslation("bindProxy"));
				}
			}
				break;
		}
	}

	public async massSignCryptoPro(signDto?: DraftDto[]): Promise<any> {
		await getUserCertificates()
			.then((certificates: Certificate[]) => {
				this.ref = this.overlayService.dialog.open(CertificateListPopupComponent, {
					data: {
						certificateList: certificates
					}
				});
				this.ref.onClose.pipe(takeUntil(this.unsubscribe$$)).subscribe(cert => {
					if (!cert) {
						this.store.dispatch(DocumentActions.signDocumentCryptoError(this.translationService.getTranslation("cancelByUser")));
						this.store.dispatch(DocumentActions.signDocumentCryptoResetPending());
						return;
					}
					const signData = signDto!;
					signData.forEach((item: DraftDto, i: number) => {
						this.signCycleCryptoPro(item, cert, signData?.length, i);
					});
				});
			})
			.catch(err => {
				this.catchCryptoSign($localize`Неподдерживаемый браузер.`);
			});
	}

	public bulkSignCryptoPro(signDto?: DraftDto[], cert?: any): void {
		if (!cert?.data?.thumbprint) {
			this.store.dispatch(DocumentActions.signDocumentCryptoError(this.translationService.getTranslation("cancelByUser")));
			this.store.dispatch(DocumentActions.signDocumentCryptoResetPending());
			return;
		}
		const signData = signDto!;
		signData.forEach((item: DraftDto, i: number) => {
			this.signCycleCryptoPro(item, cert, signData?.length, i);
		});
	}

	public openLoadingPopup(checkVersion: boolean, description = this.env.project === "ME-RU" ? "CRYPTO_PRO_ANSWER" : "CHECK_VERSION"): void {
		if (checkVersion) {
			this.overlayService.ref = this.overlayService.dialog?.open(PendingComponent, {
				data: { description },
				showHeader: false,
				width: '100%',
				height: '100%',
				closeOnEscape: false,
			});
		} else {
			if (this.overlayService.ref || this.overlayService.dialog) this.overlayService.ref?.close();
		}
	}

	public catchCryptoSign(msg?: string, showMsg = true): void {
		this.store.dispatch(DocumentActions.signDocumentCryptoError(msg!));
		this.store.dispatch(DocumentActions.signDocumentCryptoResetPending());
		if (showMsg) {
			this.showErrorMessage(msg!);
		}
	}

	private signSCCrypto(signDto: SignDto): void {
		this.userBackendService.crypto.signD
			.post$(signDto)
			.pipe(takeUntil(this.unsubscribe$$))
			.subscribe(
				signedXml => {
					if (signedXml.ErrorCode !== 0) {
						let errorMessage = signedXml.ErrorMessage;
						if (signedXml.ErrorCode === 1170) errorMessage = this.translationService.getTranslation("wrongUnp");
							this.store.dispatch(DocumentActions.signDocumentCryptoError(errorMessage, signDto?.documentType));
						} else {
							this.store.dispatch(DocumentActions.signDocumentCryptoSuccess(signedXml, `${signDto.id}`));
					}
				},
				error => this.store.dispatch(DocumentActions.signDocumentCryptoError(error, signDto?.documentType))
			);
	}

	private signNcaCrypto(signDto: SignDto): void {
    	const { xmlBase64, id, documentType, method } = signDto;
		this.openLoadingPopup(true, "SIGN");
		this.wsSubject$$ = webSocket(
				{
					url: this.wssUrl,
					openObserver: {
						next: () => {
							const signData = { ...nCASign, args: { ...nCASign.args, data: xmlBase64 } };
							this.wsSubject$$?.next(signData);
						}
					},
					closeObserver: {
						next: () => {
							this.openLoadingPopup(false);
						}
					}
				}
			);
		this.wsSubject$$.subscribe(
			(res: NCASignResponseDto) => {
				if (res?.body && !Object.keys(res?.body!)?.length) {
					this.catchCryptoSign(this.translationService.getTranslation("cancelByUser"));
					this.openLoadingPopup(false);
				}
				if (res.body?.result?.length) {
					this.openLoadingPopup(false);
					const data = res.body?.result[0]
									.replace(/-----BEGIN CMS-----/g, "")
									.replace(/-----END CMS-----/g, "")
									.replace(/\r\n/g, "")
									.replace(/\n/g, "")
									.trim();
					
					this.store.dispatch(DocumentActions.signDocumentNcaSuccess(data, String(id), documentType!, method!));
				}
			},
			(error) => {
				this.overlayService?.showNotification$(this.translationService.getTranslation("unavailableNcaLayer"), "error", undefined, undefined, true);
			}
		);
	}

	public async bindCertificateCryptoPro(): Promise<any> {
		this.openLoadingPopup(true);
		await getUserCertificates()
			.then((certificates: Certificate[]) => {
				this.ref = this.overlayService.dialog.open(CertificateListPopupComponent, {
					data: {
						certificateList: certificates,
						closePendind: () => this.openLoadingPopup(false)
					}
				});
				this.ref.onClose.pipe(takeUntil(this.unsubscribe$$)).subscribe(cert => {
					this.ref = undefined;
					if (!cert) {
						return;
					}
					this.store.dispatch(SCCryptoActions.getTokenForCertificateBinding(this.base64));
					if (this.subscription) {
						this.subscription?.unsubscribe();
					}
					this.openLoadingPopup(true, "CRYPTO_PRO_ANSWER");
					this.subscription = this.actions$.pipe(
						ofType(SCCryptoActions.getTokenForCertificateBindingSuccess),
						takeUntil(this.unsubscribe$$)
					).subscribe((token) => {
						const tokenBase64 = token?.token?.tokenBase64;
						const binaryString = atob(tokenBase64);
						const bytes = new Uint8Array(binaryString.length);
						for (let i = 0; i < binaryString.length; i++) {
							bytes[i] = binaryString.charCodeAt(i);
						}
						createHash(bytes.buffer)
							.then((hash: string) => {
								createDetachedSignature(cert.data.thumbprint, hash)
									.then((signature: string) => {
										const params: AttachSignParams = {
											subjectName: cert.data.subjectName,
											signatureBase64: signature.replace(/\r\n/g, "").replace(/\n/g, "").trim(),
											tokenBase64,
										};
										this.store.dispatch(SCCryptoActions.bindCertificateToUser(params, this.base64));
									})
									.catch(err => this.catchCryptoSign(err.message));
							})
							.catch(err => this.catchCryptoSign(err.message));
					}, error => {
						this.openLoadingPopup(false);
						this.showErrorMessage(error);
					});
				});
            })
			.catch(() => {
				this.catchCryptoSign($localize`Неподдерживаемый браузер.`);
			});
	}

	private signingMobileEds(mobileEdsMsg: MobileEdsMsg, metaData: MobileMsgMetaData): void {
		this.signatureMobileTypeOperation(mobileEdsMsg, metaData);
		this.actions$.pipe(
			ofType(SignActions.signatureMobileTypeOperationError),
			takeUntil(this.unsubscribe$$)
		).subscribe((error) => {
			this.errorProcessedDraftId = error.id;
			if (error.error.error.errorCode === "EX8013" || error.error.error.errorCode === "EX8014") {
				this.openAuthorizePopup(mobileEdsMsg, metaData);
			} else {
				this.showErrorMessage(error.error.error?.error);
			}
		});
	}

	private signatureMobileTypeOperation(mobileEdsMsg: MobileEdsMsg, metaData: MobileMsgMetaData): void {
		this.store.dispatch(
			SignActions.signatureMobileTypeOperation(
					{
						...mobileEdsMsg,
						version: "2.0"
					},
					metaData.draftType!,
					metaData.operationType!,
					true,
					String(metaData.status)!
				)
			);
		this.actions$.pipe(
			ofType(SignActions.signatureMobileTypeOperationSuccess),
			takeUntil(this.unsubscribe$$)
		).subscribe((value) => {
			const component = this.component.show(SignatureMobilePopupComponent, {
				inputs: { progressUrl: value.signatureMobile.progressUrl },
				centerPosition: true,
				zIndex: metaData.changeZIndex,
			});

			component.instance.close$
				.pipe(take(1))
				.subscribe(() => {
					this.userBackendService.crypto.getSignRequest
						.get$(value.signatureMobile.requestId, true)
						.pipe(takeUntil(this.unsubscribe$$))
						.subscribe(
							checkStatus => {
								component?.destroy();
								if (checkStatus.status === "COMPLETED") {
									if (metaData.successFn) {
										metaData.successFn();
									}
								}
							},
							(error) => this.showErrorMessage(error.error.error.error)
						);
				});
		});
	}

	private openAuthorizePopup(mobileEdsMsg: MobileEdsMsg, metaData: MobileMsgMetaData): void {
		const component = this.component?.show(CertificationMobilePopupComponent, {
			inputs: { isAuthorizeMobileEds: true },
			centerPosition: true,
			zIndex: metaData.changeZIndex,
		});

		component?.instance.close$
			.pipe(takeUntil(this.unsubscribe$$))
			.subscribe((isSuccessfulAuthorization) => {
				component?.destroy();
				if (isSuccessfulAuthorization) {
					this.signatureMobileTypeOperation(mobileEdsMsg, metaData);
					return;
				}
				++this.userRoutingService.steps;
			});
	}

	private decodeBase64Windows1251(base64String: string): string {
		const binaryString = window.atob(base64String);
		const bytes = new Uint8Array(binaryString.length);
		
		for (let i = 0; i < binaryString.length; i++) {
		  bytes[i] = binaryString.charCodeAt(i);
		}
	  
		const decoder = new TextDecoder('utf-8');

		return decoder.decode(bytes);
	}

	private checkBrowser(): boolean {
		const brands = (window.navigator as any)?.userAgentData?.brands;
		const brandsList = brands?.length && brands.map((el: { brand: string, version: string }) => el.brand);
		return brandsList?.length ? (brandsList?.includes("Google Chrome") || brandsList?.includes("Microsoft Edge")) : false;
	}

	private async signCycleCryptoPro(signDto: DraftDto, cert: any, signDtoLength: number, i: number): Promise<any> {
		const clearResult = signDto?.xmlBase64!;
		const binaryString = atob(clearResult);
		const bytes = new Uint8Array(binaryString.length);
		for (let i = 0; i < binaryString.length; i++) {
			bytes[i] = binaryString.charCodeAt(i);
		}
		await createHash(bytes.buffer)
				.then((hash: string) => {
					createDetachedSignature(cert.data.thumbprint, hash)
						.then((data: string) => {
							this.store.dispatch(
								DocumentActions.signMassIntervalDocumentCryptoSuccess(
									{
										...signDto as any,
										ResultB64: data.replace(/\r\n/g, "").replace(/\n/g, "").trim()
									},
									`${signDto?.id}`,
									signDtoLength,
									i
								)
							);
						})
						.catch(err => {this.catchCryptoSign(err.message); });
				})
				.catch(err => this.catchCryptoSign(err.message));
	}

	private async signCryptoPro1(signDto: DraftDto): Promise<any> {
		const thumbprint = signDto.cert?.data?.thumbprint || signDto.cert?.thumbprint;
		const clearResult = signDto?.xmlBase64!;
		const binaryString = atob(clearResult);
		const bytes = new Uint8Array(binaryString.length);
		for (let i = 0; i < binaryString.length; i++) {
			bytes[i] = binaryString.charCodeAt(i);
		}
		await createHash(bytes.buffer)
				.then((hash: string) => {
					createDetachedSignature(thumbprint, hash)
						.then((data: string) => {
							this.store.dispatch(
								DocumentActions.signDocumentCryptoSuccess(
									{
										...signDto as any,
										ResultB64: data.replace(/\r\n/g, "").replace(/\n/g, "").trim()
									},
									`${signDto?.id}`
								)
							);
						})
						.catch(err => {this.catchCryptoSign(err.message); });
				})
				.catch(err => this.catchCryptoSign(err.message));
	}

	private showErrorMessage(error: string): void {
		this.overlayService.showNotification$(error, "error", undefined, undefined, true);
	}
}
